import { isBefore, parseISO, subDays } from "date-fns";
import { RefreshCw, ShieldAlert, Smartphone } from "lucide-react";

import { type QuickFilter } from "@/components/QuickFilterCards";
import { type AggregatorAdmin } from "../Aggregators/components/AdminsTable/aggregator-admins-response";
import { VERSION_STATUS } from "./config";

const STALE_SYNC_DAYS = 30;

const TRANSLATION_PREFIX = "home.manager.overview.administratorsModal.quickFilters";

export type QuickFilterId =
  | "mobileUsers"
  | "latestVersion"
  | "previousVersion"
  | "oldVersion"
  | "neverLoggedIn"
  | "staleSync";

const getVersionStatus = (administrator: AggregatorAdmin) =>
  administrator.version_status?.trim() ?? "";

const hasLoggedIn = (administrator: AggregatorAdmin) => Boolean(administrator.first_login?.trim());

/**
 * An administrator who logged in but never synchronized is overdue by definition,
 * so a missing log counts as stale rather than as "no data".
 */
const hasStaleSync = (administrator: AggregatorAdmin) => {
  if (!hasLoggedIn(administrator)) {
    return false;
  }

  const lastSync = administrator.last_synchronization_log?.synchronization_date?.trim();

  if (!lastSync) {
    return true;
  }

  const syncDate = parseISO(lastSync);

  return (
    !Number.isNaN(syncDate.getTime()) && isBefore(syncDate, subDays(new Date(), STALE_SYNC_DAYS))
  );
};

/** The cards take counts, so the predicates that produce them live here. */
export type AdministratorQuickFilter = QuickFilter<QuickFilterId> & {
  matches: (administrator: AggregatorAdmin) => boolean;
};

/** Array order is the display order inside each card. */
export const ADMINISTRATOR_QUICK_FILTERS: AdministratorQuickFilter[] = [
  {
    id: "mobileUsers",
    group: "healthy",
    icon: Smartphone,
    iconClassName: "text-neutral-600 bg-neutral-100",
    labelKey: `${TRANSLATION_PREFIX}.mobileUsers`,
    matches: () => true,
  },
  {
    id: "latestVersion",
    group: "healthy",
    icon: Smartphone,
    iconClassName: "text-green-600 bg-green-100",
    labelKey: `${TRANSLATION_PREFIX}.latestVersion`,
    matches: administrator => getVersionStatus(administrator) === VERSION_STATUS.LATEST,
  },
  {
    id: "previousVersion",
    group: "healthy",
    icon: Smartphone,
    iconClassName: "text-orange-600 bg-orange-100",
    labelKey: `${TRANSLATION_PREFIX}.previousVersion`,
    matches: administrator => getVersionStatus(administrator) === VERSION_STATUS.PREVIOUS,
  },
  {
    id: "oldVersion",
    group: "attention",
    icon: Smartphone,
    iconClassName: "text-red-600 bg-red-100",
    labelKey: `${TRANSLATION_PREFIX}.oldVersion`,
    matches: administrator => getVersionStatus(administrator) === VERSION_STATUS.OLD,
  },
  {
    id: "staleSync",
    group: "attention",
    icon: RefreshCw,
    iconClassName: "text-red-600 bg-red-100",
    labelKey: `${TRANSLATION_PREFIX}.staleSync`,
    matches: hasStaleSync,
  },
  {
    id: "neverLoggedIn",
    group: "attention",
    icon: ShieldAlert,
    iconClassName: "text-red-600 bg-red-100",
    labelKey: `${TRANSLATION_PREFIX}.neverLoggedIn`,
    matches: administrator => !hasLoggedIn(administrator),
  },
];

export const countAdministratorQuickFilters = (administrators: AggregatorAdmin[]) =>
  Object.fromEntries(
    ADMINISTRATOR_QUICK_FILTERS.map(filter => [
      filter.id,
      administrators.filter(filter.matches).length,
    ])
  ) as Record<QuickFilterId, number>;
